import {useRef, useCallback, useEffect} from 'react';
import classnames from 'classnames';
import './otcInput.less';

const DEFAULT_DIGIT_COUNT = 6;

interface OtcInputProps {
    value: string;
    onChange: (value: string) => void;
    digitCount?: number;
    isDisabled?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    autoFocus?: boolean;
    isFullWidth?: boolean;
}

export default function OtcInput({
    value,
    onChange,
    digitCount = DEFAULT_DIGIT_COUNT,
    isDisabled = false,
    hasError = false,
    errorMessage,
    autoFocus = false,
    isFullWidth = false
}: OtcInputProps) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const digits = value.split('').concat(Array(digitCount).fill('')).slice(0, digitCount);

    useEffect(() => {
        if (autoFocus && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [autoFocus]);

    const focusInput = useCallback((index: number) => {
        if (index >= 0 && index < digitCount) {
            inputRefs.current[index]?.focus();
        }
    }, [digitCount]);

    function handleInputChange(index: number, inputValue: string) {
        if (isDisabled) return;
        const digit = inputValue.replace(/\D/g, '').slice(-1);
        if (!digit) return;

        const newDigits = [...digits];
        newDigits[index] = digit;
        const newValue = newDigits.join('').replace(/\s/g, '');
        onChange(newValue);

        if (index < digitCount - 1) {
            focusInput(index + 1);
        }
    }

    function handleKeyDown(index: number, event: React.KeyboardEvent) {
        if (event.key === 'Backspace') {
            event.preventDefault();
            const newDigits = [...digits];
            if (digits[index]) {
                newDigits[index] = '';
                onChange(newDigits.join('').trimEnd());
            } else if (index > 0) {
                newDigits[index - 1] = '';
                onChange(newDigits.join('').trimEnd());
                focusInput(index - 1);
            }
        } else if (event.key === 'ArrowLeft' && index > 0) {
            focusInput(index - 1);
        } else if (event.key === 'ArrowRight' && index < digitCount - 1) {
            focusInput(index + 1);
        }
    }

    function handlePaste(event: React.ClipboardEvent) {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, digitCount);
        onChange(pastedData);
        focusInput(Math.min(pastedData.length, digitCount - 1));
    }

    const containerClassName = classnames('otc-input-container', {
        error: hasError,
        disabled: isDisabled
    });

    const filledDigitsCount = value.length;
    const progressMessage = `${filledDigitsCount} of ${digitCount} digits entered`;

    return (
        <div className={containerClassName} data-wdio="test-otc-input">
            {/* eslint-disable react/no-array-index-key */}
            <div
                className={classnames('otc-digits', {'full-width': isFullWidth})}
                style={{'--otc-digit-count': digitCount} as React.CSSProperties}
                role="group"
                aria-label="Verification code"
            >
                {digits.map((digit, digitIndex) => (
                    <input
                        key={`otc-position-${digitIndex}`}
                        ref={(el) => { inputRefs.current[digitIndex] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleInputChange(digitIndex, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(digitIndex, e)}
                        onPaste={handlePaste}
                        disabled={isDisabled}
                        className={classnames('otc-digit-input', {filled: Boolean(digit)})}
                        aria-label={`Verification code digit ${digitIndex + 1} of ${digitCount}`}
                        data-wdio={`test-otc-digit-${digitIndex}`}
                    />
                ))}
            </div>
            {/* eslint-enable react/no-array-index-key */}
            <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {progressMessage}
            </div>
            {hasError && errorMessage && (
                <p className="otc-error-message" role="alert" aria-live="assertive">
                    {errorMessage}
                </p>
            )}
        </div>
    );
}
